import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppRequestRow,
  CatalogApp,
  listAppRequests,
  listAppCatalog,
  approveAppRequest,
  rejectAppRequest,
  extractApiError,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Operator-Queue fuer App-Marketplace-Anfragen (Self-Service).
 *
 * Backend:
 *   - GET  /api/app-requests?status=&tenantSlug=
 *   - POST /api/app-requests/:id/approve   (-> marketplace.install; Body optional { deploymentTarget })
 *   - POST /api/app-requests/:id/reject
 *
 * Entscheidung: einzelne Katalog-Apps/-Services werden von Tenant-Admins
 * (tenant-admin-portal) oder End-Usern (webui) ANGEFRAGT; der Deploy bleibt
 * operator-kontrolliert ("ganze Services nur ueber den Platform Admin").
 * Approve startet die Installation, Reject schliesst die Anfrage. Der
 * Anfragende kann eine noch offene Anfrage selbst zurueckziehen (cancelled).
 *
 * Die Seite pollt still nach, solange Anfragen im Status 'deploying' sind:
 * der Backend-Status-Sync (poll-on-read) schaltet sie beim naechsten GET auf
 * deployed/failed, ohne dass der Operator die Seite neu laden muss.
 */

type StatusFilter = 'pending' | 'deploying' | 'deployed' | 'rejected' | 'cancelled' | 'failed' | 'all';

const STATUS_FILTERS: StatusFilter[] = ['pending', 'deploying', 'deployed', 'rejected', 'cancelled', 'failed', 'all'];

export const STATUS_LABELS: Record<string, string> = {
  pending: 'Offen',
  approved: 'Freigegeben',
  deploying: 'Wird deployed',
  deployed: 'Deployed',
  rejected: 'Abgelehnt',
  cancelled: 'Zurückgezogen',
  failed: 'Fehlgeschlagen',
  all: 'Alle',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-sky-100 text-sky-800',
  deploying: 'bg-sky-100 text-sky-800',
  deployed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-200 text-slate-700',
  failed: 'bg-red-100 text-red-700',
};

const SOURCE_LABELS: Record<string, string> = {
  'tenant-portal': 'Tenant-Portal',
  webui: 'WebUI',
};

const SOURCE_BADGE: Record<string, string> = {
  'tenant-portal': 'bg-indigo-100 text-indigo-800',
  webui: 'bg-teal-100 text-teal-800',
};

const APP_REQUESTS_BADGE_POLL_INTERVAL_MS = 30_000;
/** Still nachladen, solange ein Deploy laeuft (Status-Sync ist poll-on-read). */
export const APP_REQUESTS_DEPLOYING_POLL_INTERVAL_MS = 15_000;

/** true, wenn mindestens eine Anfrage noch vom Deploy-Job abhaengt. */
export function hasInFlight(rows: ReadonlyArray<Pick<AppRequestRow, 'status'>>): boolean {
  return rows.some((r) => r.status === 'deploying' || r.status === 'approved');
}

/**
 * Deployment-Ziele, die der Operator beim Freigeben waehlen darf: die vom
 * Katalog deklarierten Ziele der App; ist die App (nicht mehr) im Katalog,
 * nur das bei der Anfrage gespeicherte Ziel.
 */
export function selectableTargets(row: Pick<AppRequestRow, 'appSlug' | 'deploymentTarget'>, catalog: ReadonlyArray<Pick<CatalogApp, 'slug' | 'deploymentTargets'>>): Array<'kubernetes' | 'docker'> {
  const app = catalog.find((a) => a.slug === row.appSlug);
  const declared = (app?.deploymentTargets ?? []).filter((t): t is 'kubernetes' | 'docker' => t === 'kubernetes' || t === 'docker');
  if (declared.length === 0) {
    return [row.deploymentTarget === 'docker' ? 'docker' : 'kubernetes'];
  }
  return declared;
}

export function AppRequestsPage() {
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [rows, setRows] = useState<AppRequestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<StatusFilter>('pending');
  const [tenantFilter, setTenantFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [approveRow, setApproveRow] = useState<AppRequestRow | null>(null);
  const [rejectRow, setRejectRow] = useState<AppRequestRow | null>(null);
  const [catalog, setCatalog] = useState<CatalogApp[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const refresh = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setLoading(true);
      return listAppRequests(filter, tenantFilter)
        .then((r) => {
          setRows(r.rows);
          setTotal(r.total);
          setError(null);
          setLastRefresh(new Date());
        })
        .catch((err) => setError(extractApiError(err)))
        .finally(() => {
          if (!opts?.silent) setLoading(false);
        });
    },
    [filter, tenantFilter],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Katalog einmal laden — nur fuer die Ziel-Auswahl im Approve-Dialog.
  // Fail-soft: ohne Katalog bleibt das gespeicherte Ziel der Anfrage.
  useEffect(() => {
    let cancelled = false;
    listAppCatalog()
      .then((apps) => { if (!cancelled) setCatalog(apps); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  // Stilles Nachladen, solange ein Deploy laeuft.
  const inFlight = useMemo(() => hasInFlight(rows), [rows]);
  useEffect(() => {
    if (!inFlight) return;
    const id = window.setInterval(() => { void refresh({ silent: true }); }, APP_REQUESTS_DEPLOYING_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [inFlight, refresh]);

  async function handleApprove(row: AppRequestRow, deploymentTarget?: 'kubernetes' | 'docker'): Promise<void> {
    try {
      await approveAppRequest(row.id, deploymentTarget);
      setApproveRow(null);
      setNotice(`„${row.appName}“ wird in ${row.tenantSlug} installiert.`);
      await refresh({ silent: true });
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  async function handleReject(row: AppRequestRow, reason: string): Promise<void> {
    try {
      await rejectAppRequest(row.id, reason);
      setRejectRow(null);
      setNotice(`Anfrage für „${row.appName}“ (${row.tenantSlug}) abgelehnt.`);
      await refresh({ silent: true });
    } catch (err) {
      setError(extractApiError(err));
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">App-Anfragen (Marketplace)</h2>
          <p className="text-sm text-slate-500">
            Self-Service-Anfragen aus dem Tenant-Admin-Portal und der WebUI.
            Freigeben installiert die App im Tenant; Ablehnen schliesst die Anfrage
            mit Begründung. Der Deploy bleibt operator-kontrolliert.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="search"
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            placeholder="Tenant-Slug"
            aria-label="Tenant-Filter"
            className="px-2 py-1 rounded border text-sm font-mono w-40"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            className="px-2 py-1 rounded border"
            aria-label="Status-Filter"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => { void refresh(); }}
            className="px-2 py-1 rounded border text-sm hover:bg-slate-50"
            title="Liste neu laden (Status-Sync laufender Deploys)"
          >
            Aktualisieren
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 mb-4 text-sm flex justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-700 text-xs underline">Schliessen</button>
        </div>
      )}
      {notice && !error && (
        <div role="status" className="bg-green-50 border border-green-200 text-green-800 rounded px-3 py-2 mb-4 text-sm flex justify-between gap-3">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="text-green-700 text-xs underline">Schliessen</button>
        </div>
      )}

      {loading ? (
        <p className="text-slate-500">Lade…</p>
      ) : (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left uppercase tracking-wide">
            <tr>
              <th className="p-3">Tenant</th>
              <th className="p-3">App</th>
              <th className="p-3">Quelle</th>
              <th className="p-3">Angefragt von</th>
              <th className="p-3">Eingegangen</th>
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50 align-top">
                <td className="p-3 font-mono">{r.tenantSlug}</td>
                <td className="p-3">
                  <span className="font-medium">{r.appName}</span>
                  <span className="block text-slate-500 text-xs font-mono">{r.appSlug}</span>
                  <span className="block text-slate-400 text-xs">
                    {r.category ? `${r.category} · ` : ''}Ziel: <span className="font-mono">{r.deploymentTarget}</span>
                  </span>
                </td>
                <td className="p-3">
                  <span className={`badge ${SOURCE_BADGE[r.source] ?? 'bg-slate-200'}`}>{SOURCE_LABELS[r.source] ?? r.source}</span>
                </td>
                <td className="p-3 text-xs text-slate-600">
                  {r.requestedByEmail}
                  {r.note && (
                    <span className="block mt-1 text-slate-500 border-l-2 border-slate-200 pl-2 whitespace-pre-wrap" title="Notiz des Anfragenden">
                      „{r.note}“
                    </span>
                  )}
                </td>
                <td className="p-3 text-slate-500" title={new Date(r.createdAt).toLocaleString()}>
                  {relativeAge(r.createdAt)}
                </td>
                <td className="p-3">
                  <span className={`badge ${STATUS_BADGE[r.status] ?? 'bg-slate-200'}`}>{statusLabel(r.status)}</span>
                  {r.status === 'failed' && r.failureReason && (
                    <span className="block text-red-600 text-xs mt-1">{r.failureReason}</span>
                  )}
                  {(r.status === 'rejected' || r.status === 'cancelled') && r.decisionNote && (
                    <span className="block text-slate-500 text-xs mt-1">{r.decisionNote}</span>
                  )}
                  {r.decidedAt && r.status !== 'pending' && (
                    <span className="block text-slate-400 text-xs mt-1" title={new Date(r.decidedAt).toLocaleString()}>
                      entschieden {relativeAge(r.decidedAt)}
                    </span>
                  )}
                </td>
                <td className="p-3 text-right space-x-2 whitespace-nowrap">
                  {r.status === 'pending' ? (
                    <>
                      <button
                        onClick={() => setApproveRow(r)}
                        disabled={isReadOnly}
                        className="px-2 py-1 rounded bg-green-600 text-white text-xs hover:bg-green-700 disabled:opacity-40"
                        title={isReadOnly ? 'Read-only-Operator' : 'Freigeben & App installieren'}
                      >
                        Freigeben
                      </button>
                      <button
                        onClick={() => setRejectRow(r)}
                        disabled={isReadOnly}
                        className="px-2 py-1 rounded border border-red-300 text-red-700 text-xs hover:bg-red-50 disabled:opacity-40"
                        title={isReadOnly ? 'Read-only-Operator' : 'Anfrage mit Begründung ablehnen'}
                      >
                        Ablehnen
                      </button>
                    </>
                  ) : (
                    <span className="text-slate-400 text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">Keine Anfragen für diesen Filter.</td></tr>
            )}
          </tbody>
        </table>
      )}
      <p className="text-xs text-slate-400 mt-2">
        {total} Anfrage(n) gesamt im Filter.
        {lastRefresh && <> Stand {lastRefresh.toLocaleTimeString()}.</>}
        {inFlight && <> Deploy läuft — Liste wird automatisch aktualisiert.</>}
      </p>

      {approveRow && (
        <ApproveModal
          row={approveRow}
          targets={selectableTargets(approveRow, catalog)}
          onCancel={() => setApproveRow(null)}
          onConfirm={handleApprove}
        />
      )}
      {rejectRow && (
        <RejectModal row={rejectRow} onCancel={() => setRejectRow(null)} onConfirm={handleReject} />
      )}
    </div>
  );
}

export function AppRequestsNavBadge() {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    function load() {
      listAppRequests('pending')
        .then((r) => { if (!cancelled) setCount(r.total); })
        .catch(() => { if (!cancelled) setCount(null); });
    }
    load();
    const id = window.setInterval(load, APP_REQUESTS_BADGE_POLL_INTERVAL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);
  if (count === null || count === 0) return null;
  return (
    <span
      className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
      aria-label={`${count} offene App-Anfragen`}
      title={`${count} offene App-Anfragen`}
    >
      {count}
    </span>
  );
}

interface ApproveModalProps {
  row: AppRequestRow;
  targets: Array<'kubernetes' | 'docker'>;
  onCancel: () => void;
  onConfirm: (row: AppRequestRow, deploymentTarget?: 'kubernetes' | 'docker') => void | Promise<void>;
}

function ApproveModal({ row, targets, onCancel, onConfirm }: ApproveModalProps) {
  const [busy, setBusy] = useState(false);
  const initial = targets.includes(row.deploymentTarget as 'kubernetes' | 'docker')
    ? (row.deploymentTarget as 'kubernetes' | 'docker')
    : targets[0];
  const [target, setTarget] = useState<'kubernetes' | 'docker'>(initial);
  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    try {
      // Nur senden, wenn der Operator vom gespeicherten Ziel abweicht.
      await onConfirm(row, target !== row.deploymentTarget ? target : undefined);
    } finally { setBusy(false); }
  }
  return (
    <ModalShell onClose={onCancel} ariaLabel="App-Anfrage freigeben">
      <h3 className="text-lg font-semibold mb-2">Anfrage freigeben?</h3>
      <p className="text-sm text-slate-600 mb-3">
        Installiert <strong>{row.appName}</strong> (<span className="font-mono">{row.appSlug}</span>)
        im Tenant <span className="font-mono">{row.tenantSlug}</span> und startet den Marketplace-Install-Job.
        Angefragt von <span className="font-mono">{row.requestedByEmail}</span> ({SOURCE_LABELS[row.source] ?? row.source}).
      </p>
      {row.note && (
        <p className="text-xs text-slate-500 mb-4 border-l-2 border-slate-200 pl-2 whitespace-pre-wrap">„{row.note}“</p>
      )}
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-slate-700">Deployment-Ziel</span>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value as 'kubernetes' | 'docker')}
            disabled={targets.length < 2}
            className="mt-1 w-full px-2 py-1 border rounded text-sm font-mono"
            aria-label="Deployment-Ziel"
          >
            {targets.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="block text-xs text-slate-400 mt-1">
            {targets.length < 2
              ? 'Die App unterstützt nur dieses Ziel.'
              : 'Vorbelegt mit dem bei der Anfrage ermittelten Ziel; Tenants laufen als vCluster (kubernetes).'}
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={busy} className="px-3 py-1 rounded border">Abbrechen</button>
          <button type="submit" disabled={busy} className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
            {busy ? 'Installiere…' : 'Freigeben & installieren'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

interface RejectModalProps {
  row: AppRequestRow;
  onCancel: () => void;
  onConfirm: (row: AppRequestRow, reason: string) => void | Promise<void>;
}

function RejectModal({ row, onCancel, onConfirm }: RejectModalProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    try { await onConfirm(row, reason.trim()); } finally { setBusy(false); }
  }
  return (
    <ModalShell onClose={onCancel} ariaLabel="App-Anfrage ablehnen">
      <h3 className="text-lg font-semibold mb-2">Anfrage ablehnen?</h3>
      <p className="text-sm text-slate-600 mb-4">
        Schliesst die Anfrage für <strong>{row.appName}</strong> (Tenant{' '}
        <span className="font-mono">{row.tenantSlug}</span>) ohne Installation.
        Die Begründung sieht der Anfragende in seiner Anfrage-Liste.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-slate-700">Ablehnungsgrund (erforderlich)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} required className="mt-1 w-full px-2 py-1 border rounded text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={busy} className="px-3 py-1 rounded border">Abbrechen</button>
          <button type="submit" disabled={busy || !reason.trim()} className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            {busy ? 'Lehne ab…' : 'Ablehnen'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ children, onClose, ariaLabel }: { children: React.ReactNode; onClose: () => void; ariaLabel: string }) {
  useEffect(() => {
    function onKey(ev: KeyboardEvent) { if (ev.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded shadow-xl w-full max-w-lg p-5">{children}</div>
    </div>
  );
}

export function relativeAge(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '—';
  const delta = now - new Date(iso).getTime();
  if (Number.isNaN(delta)) return iso;
  const m = Math.floor(delta / 60_000);
  if (m < 1) return 'gerade eben';
  if (m < 60) return `vor ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h}h`;
  return `vor ${Math.floor(h / 24)}d`;
}
